import { useMemo, type ComponentType, type ReactNode } from 'react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { useModules } from '@/hooks/use-modules';
import { useHomePreference } from '@/hooks/use-home-preference';
import { useAuthStore } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import {
  HOME_PAGES,
  availableHomePages,
  homePageAllowsRole,
  resolveHomePage,
  type HomePageId,
} from './home/home-registry';
import { HomeSwitcher } from './home/home-switcher';
import { PosHome } from './home/pos-home';
import { ReceivePaymentsHome } from './home/receive-payments-home';
import { SendPaymentsHome } from './home/send-payments-home';
import { QuickLinks } from './home/quick-links';

/** The view behind each registry entry. Register new module homes here. */
const HOME_VIEWS: Record<HomePageId, ComponentType<{ switcher?: ReactNode }>> = {
  pos: PosHome,
  'receive-payments': ReceivePaymentsHome,
  'send-payments': SendPaymentsHome,
};

/**
 * Picks which home to show:
 *
 *  1. the pages available here — module active at the selected location and
 *     the role allowed (see home-registry.ts);
 *  2. the user's saved default if it's one of them, else the first available
 *     (registry order keeps POS first, so tenants with POS on see exactly
 *     what they saw before module homes existed);
 *  3. nothing available → the shortcut grid.
 *
 * With more than one page available the header carries a switcher that
 * saves the choice. With no location selected there's no module state to
 * read, so analytics roles get the POS layout (which carries the "choose a
 * location" prompt) and everyone else the shortcut grid — as before.
 */
export function HomePage() {
  const { user } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const role = user?.role;
  const { hasAny } = usePermissions();

  const { data: modulesData, isLoading: modulesLoading } = useModules(selectedLocationId);
  const { data: prefData, isLoading: prefLoading } = useHomePreference();

  const modules = modulesData?.data;
  const available = useMemo(() => {
    if (selectedLocationId) return availableHomePages(modules ?? [], role, hasAny);
    const pos = HOME_PAGES.find((p) => p.id === 'pos')!;
    return homePageAllowsRole(pos, role) ? [pos] : [];
  }, [modules, role, selectedLocationId, hasAny]);

  // "Welcome back" rather than a time-of-day greeting: the wording is part of
  // the app's public surface, asserted by the login smoke test in
  // qa-automation, and changing it bought nothing worth breaking that for.
  const firstName = user?.name?.split(' ')[0] || 'there';

  // Until the location's modules and the saved preference load we can't tell
  // which view applies; show just the header rather than flashing one layout
  // and swapping to another.
  const pending = (!!selectedLocationId && modulesLoading) || prefLoading;
  if (pending) {
    return (
      <div className="space-y-6">
        <PageHeader title={`Welcome back, ${firstName}`} description="Here's an overview of your business dashboard">
          <LocationBadge />
        </PageHeader>
      </div>
    );
  }

  const active = resolveHomePage(available, prefData?.data?.defaultHomePage);
  if (!active) {
    return (
      <div className="space-y-6">
        <PageHeader title={`Welcome back, ${firstName}`} description="Here's an overview of your business dashboard">
          <LocationBadge />
        </PageHeader>
        <QuickLinks />
      </div>
    );
  }

  const View = HOME_VIEWS[active.id];
  const switcher = available.length > 1 ? <HomeSwitcher pages={available} activeId={active.id} /> : undefined;
  return <View switcher={switcher} />;
}
